import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ChevronLeftStrokeIcon,
  ChevronRightStrokeIcon,
  Preloader,
  useResponsive,
} from '@common-fe/common-fe';
import lodash from 'lodash';
import styled from 'styled-components';

import { FileImage, FileType } from '@/common/constants';
import { DocumentPagePreview } from '@/components/controls/DropScan/dropScan.types';
import { PinchZoomPan, PinchZoomPanInstance } from '@/components/elements/vendor/PinchZoomPan';
import { WrapperWithVerticalShadows } from '@/components/wrappers/WrapperWithVerticalShadows';
import { useDetectVerticalScrollShadow } from '@/utils/hooks/useDetectVerticalScrollShadow';

import DropWrapper from '../DropWrapper';
import ImageZoomer, { ZoomerProps } from '../ImageZoomer';
import prepareFile from '../prepareFile';

import PdfScanViewer from './PdfScanViewer/PdfScanViewer';
import TiffScanViewer from './TiffScanViewer/TiffScanViewer';
import ScanPagePreview from './ScanPagePreview';
import { PreviewTooltip, ScanPreview } from './ScanPreview';
import { useThumbsScroll } from './useThumbsScroll';

const ScanUpload = lazy(() => import('../ScanUpload'));

interface ThumbsProps {
  isSmallViewVersion?: boolean;
  isThumbsTopPosition?: boolean;
}

const Thumbs = styled.div<ThumbsProps>`
  ${({ theme }) => theme.mixins.customLightScroll};
  overflow-y: ${({ isSmallViewVersion, isThumbsTopPosition }) =>
    isThumbsTopPosition ? 'hidden' : isSmallViewVersion ? 'unset' : 'auto'};
  overflow-x: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? 'auto' : 'unset')};

  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? '0px' : '8px')};
    height: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? '0px' : 'unset')};
  }
`;

interface ThumbsInnerProps {
  isThumbsTopPosition?: boolean;
  $isScrollable?: boolean;
}

const ThumbsInner = styled.div<ThumbsInnerProps>`
  display: flex;
  flex-direction: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? 'row' : 'column')};
  align-items: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? 'center' : 'stretch')};
  gap: 8px;

  padding: ${({ isThumbsTopPosition, $isScrollable }) =>
    isThumbsTopPosition ? ($isScrollable ? '12px 40px' : '12px 0') : '8px'};

  width: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? 'max-content' : '100%')};

  & > * {
    flex-shrink: 0;
  }

  & > div {
    display: flex;
    flex-direction: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? 'row' : 'column')};
    gap: 2px;
    align-items: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? 'center' : 'stretch')};

    & > * {
      flex-shrink: 0;
      margin: 0;
    }
  }

  & > div > div {
    display: flex;
    flex-direction: ${({ isThumbsTopPosition }) => (isThumbsTopPosition ? 'row' : 'column')};
    gap: 8px;

    & > * {
      flex-shrink: 0;
      margin: 0;
    }
  }
`;

const RoundedImg = styled.img<{ $hideShadow?: boolean }>`
  max-height: 100%;
  max-width: 100%;
  ${({ theme, $hideShadow }) => !$hideShadow && theme.shadows.active};
  border-radius: ${({ theme }) => theme.rounds.container2Round};
`;

interface SliderPositionProps {
  $position: 'left' | 'right';
  disabled?: boolean;
}

const SliderButton = styled.button<SliderPositionProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $position }) => ($position === 'left' ? 'left: 0;' : 'right: 0;')}
  width: 40px;
  height: 72px;
  border: none;
  background: transparent;
  cursor: pointer;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: ${({ $position }) => ($position === 'left' ? 'flex-start' : 'flex-end')};
  padding: 0;

  &:disabled {
    cursor: default;
  }
`;

const SliderIconWrapper = styled.div<SliderPositionProps>`
  width: 24px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.module};
  border-radius: ${({ $position }) => ($position === 'left' ? '0 4px 4px 0' : '4px 0 0 4px')};
  transition: background 0.15s;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};

  ${SliderButton}:not(:disabled):hover & {
    background: ${({ theme }) => theme.colors.accentContainer};
  }
`;

const SliderGradient = styled.div<SliderPositionProps>`
  width: 16px;
  height: 72px;
  background: ${({ $position, theme }) =>
    $position === 'left'
      ? `linear-gradient(90deg, ${theme.colors.module} 0%, transparent 100%)`
      : `linear-gradient(270deg, ${theme.colors.module} 0%, transparent 100%)`};
  opacity: ${({ disabled }) => (disabled ? 0 : 1)};
  transition: opacity 0.15s;
`;

const StickyThumbs = styled(Box)<{ $enabled?: boolean; $position?: 'top' | 'bottom' }>`
  position: ${({ $enabled }) => ($enabled ? 'sticky' : 'static')};
  ${({ $position }) => ($position === 'bottom' ? 'bottom: 0;' : 'top: 0;')}
  z-index: 2;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.module};
`;

interface Props {
  storeKey?: string;
  isPreviewHidden?: boolean;
  uploadButton?: React.ReactNode;
  qrCodeButton?: React.ReactNode;
  hasMainLabel?: boolean;
  isActiveFileShown?: boolean;
  isShowTooltip?: boolean;
  noAddedFiles?: React.ReactNode;
  isSmallViewVersion?: boolean;
  isFlex?: boolean;
  isAddDocFirst?: boolean;
  disableUpload?: boolean;
  isRemovingDisabled?: boolean;
  activeFile?: number;
  defaultFiles?: FileImage[];
  onSetFiles?: (values: FileImage[]) => void;
  onSetActivePage?: (index: number) => void;
  onSetPreviewMode?: () => void;
  addFileButtonRef?: React.RefObject<HTMLDivElement | null>;
  handleDeleteFile?: (id: string) => void;
  onFileUploadError?: () => void;
  zoomerProps?: ZoomerProps;
  isThumbsBottom?: boolean;
  isNumberOfImageHidden?: boolean;
  isWithThumbs?: boolean;
  pdfMinHeight?: string;
  pdfWithoutToolbar?: boolean;
  showPagesInPreview?: boolean;
  ocrRequired?: boolean;
  isThumbsTopPosition?: boolean;
  rotation?: number;
  overrideFiles?: FileImage[];
  externalScale?: number;
  pinchZoomRef?: React.RefObject<PinchZoomPanInstance | null>;
  hideShadow?: boolean;
  onScaleChange?: (scale: number) => void;
  allowPasswordEntry?: boolean;
}

const ScanPreviewContainer: React.FC<Props> = ({
  storeKey,
  isPreviewHidden,
  uploadButton,
  hasMainLabel,
  isActiveFileShown,
  noAddedFiles,
  isSmallViewVersion,
  isAddDocFirst,
  defaultFiles,
  onSetFiles,
  onSetActivePage,
  onSetPreviewMode,
  addFileButtonRef,
  isShowTooltip = true,
  activeFile = 0,
  disableUpload,
  isRemovingDisabled,
  handleDeleteFile,
  onFileUploadError,
  isFlex,
  zoomerProps,
  qrCodeButton,
  isThumbsBottom = false,
  isNumberOfImageHidden,
  isWithThumbs = true,
  pdfMinHeight,
  pdfWithoutToolbar,
  showPagesInPreview,
  ocrRequired,
  isThumbsTopPosition,
  rotation = 0,
  overrideFiles,
  externalScale,
  pinchZoomRef,
  hideShadow,
  onScaleChange,
  allowPasswordEntry,
}) => {
  const { isMobile } = useResponsive();
  const [showTooltip, setShowTooltip] = useState(isShowTooltip);
  const [files, setFiles] = React.useState<FileImage[]>(defaultFiles || []);

  const prevFilesLength = useRef(defaultFiles?.length || 0);
  const [activePage, setActivePage] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (!lodash.isEqual(files, defaultFiles)) {
      const nextFiles = (defaultFiles || []).map((file) => {
        if (!file.preview && !file.url && typeof window !== 'undefined') {
          try {
            return prepareFile(file as any);
          } catch (e) {
            return file;
          }
        }
        return file;
      });

      setFiles(nextFiles);

      if (nextFiles.length > prevFilesLength.current && activeFile === undefined) {
        const newIndex = nextFiles.length - 1;
        setActivePage(newIndex);
        if (onSetActivePage) {
          onSetActivePage(newIndex);
        }
      }
      prevFilesLength.current = nextFiles.length;
    }
  }, [defaultFiles, activeFile, onSetActivePage]);

  React.useEffect(() => {
    if (!lodash.isEqual(files, defaultFiles) && onSetFiles) {
      onSetFiles(files || []);
    }
  }, [files, onSetFiles]);

  React.useEffect(() => {
    if (activeFile !== undefined) {
      setActivePage(activeFile);
    }
  }, [activeFile]);

  const safeActivePage = useMemo(() => {
    if (activePage !== null && activePage < files.length) {
      return activePage;
    }
    return files.length > 0 ? files.length - 1 : null;
  }, [activePage, files.length]);

  const isPDF = React.useMemo(
    () => safeActivePage !== null && files[safeActivePage]?.type === FileType.PDF,
    [files, safeActivePage]
  );

  const isTif = React.useMemo(
    () =>
      safeActivePage !== null &&
      (files[safeActivePage]?.type === FileType.TIF || files[safeActivePage]?.type === FileType.TIFF),
    [files, safeActivePage]
  );

  const shouldShowPages = useMemo(
    () => showPagesInPreview && (isPDF || isTif),
    [isPDF, isTif, showPagesInPreview]
  );

  const handleSetFiles = React.useCallback(
    (acceptedFiles: FileImage[]) => {
      setFiles((state) => {
        const nextFiles = state.concat(acceptedFiles.map(prepareFile));
        const newIndex = nextFiles.length - 1;
        setActivePage(newIndex);
        if (onSetActivePage) {
          onSetActivePage(newIndex);
        }
        return nextFiles;
      });
    },
    [onSetActivePage]
  );

  const handleDelete = React.useCallback(
    (index: number) => {
      if (files[index]) {
        URL.revokeObjectURL(files[index].preview);
        if (handleDeleteFile) handleDeleteFile(files[index].id);
      }
      const newlist = files.filter((_, i) => i !== index);
      setFiles(newlist);
      setActivePage(0);

      if (onSetActivePage) {
        onSetActivePage(0);
      }
    },
    [files, onSetActivePage, handleDeleteFile]
  );

  const { thumbsRef, isScrollable, canScrollLeft, canScrollRight, updateScrollState, scrollThumbs } =
    useThumbsScroll({
      filesLength: files.length,
      isSmallViewVersion,
      isThumbsTopPosition,
    });

  const { onScrollHandler, scrolledToTheEnd, startedScrolling } = useDetectVerticalScrollShadow(
    thumbsRef.current
  );

  const handleThumbsScroll = useCallback(
    (e?: React.UIEvent<HTMLDivElement>) => {
      updateScrollState();
      if (e && onScrollHandler) {
        onScrollHandler(e);
      }
    },
    [updateScrollState, onScrollHandler]
  );

  const isShadowAvailable = useMemo(
    () => !isSmallViewVersion && !isThumbsTopPosition && files.length > 4,
    [files, isSmallViewVersion, isThumbsTopPosition]
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [documentPagePreviews, setDocumentPagePreviews] = useState<DocumentPagePreview[]>();
  const [activeDocumentPage, setActiveDocumentPage] = useState(0);

  const handleScrollToDocumentPage = useCallback(
    (documentPagePreview: DocumentPagePreview, pageIndex: number) => {
      if (scrollContainerRef.current && documentPagePreview?.pageRef?.current) {
        setActiveDocumentPage(pageIndex);
        scrollContainerRef?.current?.scrollTo({
          top: documentPagePreview?.pageRef?.current?.offsetTop || 0,
          behavior: 'smooth',
        });
      }
    },
    []
  );

  useEffect(() => {
    setActiveDocumentPage(0);
  }, [files]);

  const documentViewer = useMemo(() => {
    if (safeActivePage === null || !files[safeActivePage]) {
      return null;
    }

    const activeFileObj = files[safeActivePage];
    const fileUrl = activeFileObj.url || activeFileObj.preview;

    const rotationStyle: React.CSSProperties = {
      transform: `rotate(${rotation}deg)`,
      transition: 'transform 0.3s ease',
      transformOrigin: 'center center',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    };

    if (isPDF) {
      return (
        <Box style={rotationStyle}>
          <PdfScanViewer
            url={fileUrl}
            onSetPreviews={setDocumentPagePreviews}
            allowPasswordEntry={allowPasswordEntry}
          />
        </Box>
      );
    }

    if (isTif) {
      return (
        <Box style={rotationStyle}>
          <TiffScanViewer url={fileUrl} onSetPreviews={setDocumentPagePreviews} />
        </Box>
      );
    }

    return (
      // @ts-ignore
      <PinchZoomPan
        ref={pinchZoomRef}
        doubleTapBehavior="zoom"
        position="center"
        initialScale={externalScale || 1}
        minScale={0.1}
        maxScale={4}
        zoomButtons={!isPDF && !isMobile}
        isInCenter={isThumbsTopPosition}
        onScaleChange={onScaleChange}
      >
        <Box>
          <RoundedImg
            $hideShadow={hideShadow}
            src={activeFileObj.preview || activeFileObj.url}
            alt={activeFileObj.name}
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.3s ease',
              transformOrigin: 'center center',
            }}
          />
        </Box>
      </PinchZoomPan>
    );
  }, [safeActivePage, files, isPDF, isTif, isMobile, isThumbsTopPosition, rotation, allowPasswordEntry]);

  const thumbsBlock = useMemo(() => {
    return (
      <Box style={{ position: 'relative', width: isThumbsTopPosition ? '100%' : 'fit-content' }}>
        {!isSmallViewVersion && showTooltip && <PreviewTooltip setShowTooltip={setShowTooltip} />}

        {isThumbsTopPosition && isScrollable && files.length > 0 && (
          <>
            <SliderButton $position="left" disabled={!canScrollLeft} onClick={() => scrollThumbs('left')}>
              <SliderIconWrapper $position="left" disabled={!canScrollLeft}>
                <ChevronLeftStrokeIcon />
              </SliderIconWrapper>
              <SliderGradient $position="left" disabled={!canScrollLeft} />
            </SliderButton>

            <SliderButton $position="right" disabled={!canScrollRight} onClick={() => scrollThumbs('right')}>
              <SliderGradient $position="right" disabled={!canScrollRight} />
              <SliderIconWrapper $position="right" disabled={!canScrollRight}>
                <ChevronRightStrokeIcon />
              </SliderIconWrapper>
            </SliderButton>
          </>
        )}

        <WrapperWithVerticalShadows
          notAllowed={!isShadowAvailable}
          startedScrolling={startedScrolling}
          scrolledToTheEnd={isShadowAvailable ? scrolledToTheEnd : true}
        >
          <Thumbs
            ref={thumbsRef}
            isSmallViewVersion={isSmallViewVersion}
            isThumbsTopPosition={isThumbsTopPosition}
            onScroll={handleThumbsScroll}
          >
            <ThumbsInner isThumbsTopPosition={isThumbsTopPosition} $isScrollable={isScrollable}>
              {shouldShowPages ? (
                <ScanPagePreview
                  isSmallViewVersion={isSmallViewVersion}
                  isFlex={isFlex}
                  isMobile={isMobile}
                  activePage={activeDocumentPage}
                  documentPagesPreview={documentPagePreviews}
                  onSelectPage={handleScrollToDocumentPage}
                />
              ) : (
                <ScanPreview
                  isNumberOfImageHidden={isNumberOfImageHidden}
                  storeKey={storeKey}
                  defaultFiles={files}
                  files={files}
                  overrideFiles={overrideFiles}
                  isPreviewHidden={isPreviewHidden}
                  hasMainLabel={hasMainLabel}
                  isSmallViewVersion={isSmallViewVersion}
                  isFlex={isFlex}
                  onSelect={(index: number) => {
                    setActivePage(index);
                    if (onSetPreviewMode) onSetPreviewMode();
                    if (onSetActivePage) {
                      onSetActivePage(index);
                    }
                  }}
                  {...(isRemovingDisabled ? {} : { onDelete: handleDelete })}
                  selectedIndex={safeActivePage}
                  isAddDocFirst={isAddDocFirst}
                  isThumbsTopPosition={isThumbsTopPosition}
                >
                  {!disableUpload && (
                    <Suspense fallback={<Preloader />}>
                      <ScanUpload
                        onFilesAdded={handleSetFiles}
                        onFileUploadError={onFileUploadError}
                        addFileButtonRef={addFileButtonRef}
                        uploadButton={uploadButton}
                        storeKey={storeKey}
                        ocrRequired={ocrRequired}
                      />
                    </Suspense>
                  )}
                  {!disableUpload && qrCodeButton}
                </ScanPreview>
              )}
            </ThumbsInner>
          </Thumbs>
        </WrapperWithVerticalShadows>
      </Box>
    );
  }, [
    isSmallViewVersion,
    showTooltip,
    isShadowAvailable,
    startedScrolling,
    scrolledToTheEnd,
    storeKey,
    isPreviewHidden,
    hasMainLabel,
    isFlex,
    safeActivePage,
    isRemovingDisabled,
    handleDelete,
    isAddDocFirst,
    disableUpload,
    handleSetFiles,
    onFileUploadError,
    uploadButton,
    qrCodeButton,
    onSetPreviewMode,
    onSetActivePage,
    addFileButtonRef,
    handleThumbsScroll,
    isNumberOfImageHidden,
    shouldShowPages,
    isMobile,
    documentPagePreviews,
    handleScrollToDocumentPage,
    activeDocumentPage,
    ocrRequired,
    isThumbsTopPosition,
    canScrollLeft,
    canScrollRight,
    isScrollable,
    scrollThumbs,
    files,
  ]);

  return (
    <DropWrapper
      isSmallViewVersion={isSmallViewVersion}
      isFlex={isFlex}
      isThumbsTopPosition={isThumbsTopPosition}
      data-testId="drop-wrapper"
    >
      {!isThumbsBottom && isWithThumbs && (
        <StickyThumbs $enabled={isThumbsTopPosition} $position="top">
          {thumbsBlock}
        </StickyThumbs>
      )}
      {safeActivePage !== null && files[safeActivePage] && isActiveFileShown && (
        <>
          {isSmallViewVersion &&
            files.map((file, index) => (
              <Box key={file.preview} {...(index !== safeActivePage ? { style: { display: 'none' } } : {})}>
                <ImageZoomer key={file.id} file={file} zoomerProps={zoomerProps} />
              </Box>
            ))}

          {!isSmallViewVersion && (
            <Box
              ref={scrollContainerRef}
              data-testid="document-preview-scroll-container"
              overflow={{ vertical: 'auto' }}
              width="100%"
              style={{ position: 'relative' }}
            >
              {documentViewer}
            </Box>
          )}
        </>
      )}

      {isThumbsBottom && isWithThumbs && thumbsBlock}

      {files.length === 0 && noAddedFiles && noAddedFiles}
    </DropWrapper>
  );
};

export default ScanPreviewContainer;
